import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Server, Users, Shield, Map, Wifi, Copy, Check, Play, Zap, Info, 
  MessageSquare, ExternalLink, BookOpen, ShieldCheck, Gamepad2, Hash, 
  Sparkles, LayoutGrid, List, Terminal, Filter
} from "lucide-react";
import ServerDetailsModal from "../components/ServerDetailsModal";
import { ServerStatusRow, ServerSummaryFooter } from "../components/ServerStatusWidget";
import api from "../lib/api";
import { toast } from "sonner";

const DEFAULT_SERVERS = [
  { id: "srv_1", address: "209.38.247.243:27015", name: "OFFICIAL.INDUNGI.PRO - RESPAWN CSDM", game_type: "cs16", mod: "RESPAWN", dns: "official.indungi.pro" },
  { id: "srv_2", address: "209.38.247.243:27016", name: "DOPE.INDUNGI.PRO - CLASIC / PUBLIC", game_type: "cs16", mod: "PUBLIC", dns: "dope.indungi.pro" },
  { id: "srv_3", address: "209.38.247.243:27017", name: "MIX.INDUNGI.PRO - OFFICIAL MIX 5v5", game_type: "cs16", mod: "MIX", dns: "mix.indungi.pro" },
  { id: "srv_4", address: "209.38.247.243:27018", name: "WAR3.INDUNGI.PRO - WAR3FT HEROES", game_type: "cs16", mod: "WAR3FT", dns: "war3.indungi.pro" },
  { id: "srv_5", address: "209.38.247.243:27019", name: "FURIEN.INDUNGI.PRO - FURIEN + CREDITS", game_type: "cs16", mod: "FUN", dns: "furien.indungi.pro" },
  { id: "srv_6", address: "209.38.247.243:27020", name: "ZM.INDUNGI.PRO - ZOMBIE PLAGUE 2026", game_type: "cs16", mod: "FUN", dns: "zm.indungi.pro" },
  { id: "srv_7", address: "CrGC77X8W", name: "INDUNGI NETWORK DISCORD", game_type: "discord", mod: "DISCORD", dns: "discord.gg/CrGC77X8W" },
];

const MAP_IMAGES = {
  de_dust2: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_inferno: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_nuke: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_cbble: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_train: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_aztec: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_kabul: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_westwood: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_alexandra: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_tuscan: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  cs_assault: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  cs_militia: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  cs_747: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_mirage: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  cs_office: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  cs_italy: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  fy_snow: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  fy_iceworld: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  awp_bycastor32: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  awp_india: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  default: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg")
};

const getMapImage = (mapName) => {
  if (!mapName) return MAP_IMAGES.default;
  const m = mapName.toLowerCase().trim();
  if (m.includes("dust2") || m.includes("dust")) return MAP_IMAGES.de_dust2;
  if (m.includes("inferno")) return MAP_IMAGES.de_inferno;
  if (m.includes("cbble") || m.includes("cobble")) return MAP_IMAGES.de_cbble;
  if (m.includes("train")) return MAP_IMAGES.de_train;
  if (m.includes("aztec")) return MAP_IMAGES.de_aztec;
  if (m.includes("kabul")) return MAP_IMAGES.de_kabul;
  if (m.includes("westwood")) return MAP_IMAGES.de_westwood;
  if (m.includes("alexandra")) return MAP_IMAGES.de_alexandra;
  if (m.includes("tuscan")) return MAP_IMAGES.de_tuscan;
  if (m.includes("assault")) return MAP_IMAGES.cs_assault;
  if (m.includes("militia")) return MAP_IMAGES.cs_militia;
  if (m.includes("snow")) return MAP_IMAGES.fy_snow;
  if (m.includes("iceworld")) return MAP_IMAGES.fy_iceworld;
  if (m.includes("india") || m.includes("bycastor")) return MAP_IMAGES.awp_india;
  if (m.includes("747")) return MAP_IMAGES.cs_747;
  if (m.includes("nuke")) return MAP_IMAGES.de_nuke;
  if (m.includes("mirage")) return MAP_IMAGES.de_mirage;
  if (m.includes("office")) return MAP_IMAGES.cs_office;
  if (m.includes("italy")) return MAP_IMAGES.cs_italy;
  return `https://image.gametracker.com/images/maps/160x120/cs/${m}.jpg`;
};

function DiscordSidebarCard({ inviteCode = "CrGC77X8W" }) {
  const [copied, setCopied] = useState(false);

  const { data } = useQuery({
    queryKey: ['discordStatusSidebarReal', inviteCode],
    queryFn: async () => {
      const res = await fetch(`/api/discord/status?code=${inviteCode}`);
      return res.json();
    },
    staleTime: 0,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const invite = data?.instant_invite || `https://discord.gg/${inviteCode}`;
  const guildName = data?.name || "INDUNGI NETWORK";
  const iconUrl = data?.icon_url || "https://cdn.discordapp.com/icons/1193299540019794161/eb3c6c5650b82001b8488949bfc30c68.png?size=256";
  const bannerUrl = data?.banner_url || "https://cdn.discordapp.com/banners/1193299540019794161/bf24a53b3c1909a67ef12476c49bd885.png?size=600";
  const traits = data?.traits || ["CS 1.6", "MIX 5v5", "WAR3FT", "RESPAWN", "COMMUNITY"];

  const handleCopy = () => {
    navigator.clipboard.writeText(invite);
    setCopied(true);
    toast.success(`Invite Discord copiat: ${invite}`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleJoin = () => {
    window.open(invite, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-[#0e1018] border border-[#5865F2]/40 rounded-2xl overflow-hidden shadow-2xl space-y-0">
      <div className="relative h-28 w-full bg-zinc-950 overflow-hidden flex items-end p-4">
        {bannerUrl ? (
          <img src={bannerUrl} alt={guildName} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-[#5865F2]/40 via-[#5865F2]/20 to-[#0e1018]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1018] via-[#0e1018]/50 to-transparent" />

        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono font-bold shadow-md backdrop-blur">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          ONLINE LIVE
        </div>

        <div className="relative -mb-8 z-20 w-14 h-14 rounded-xl overflow-hidden border-2 border-[#5865F2] bg-black shadow-xl shrink-0">
          <img src={iconUrl} alt={guildName} className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="p-4 pt-10 space-y-3 font-mono">
        <div>
          <h3 className="font-chakra font-black text-sm uppercase text-white tracking-wide">
            {guildName}
          </h3>
          <p className="text-[11px] text-zinc-400">Canalul oficial Discord al comunității CS 1.6</p>
        </div>

        <div className="flex flex-wrap gap-1 pt-1">
          {traits.map((t, idx) => (
            <span key={idx} className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 text-[9px] font-bold">
              {t}
            </span>
          ))}
        </div>

        <div className="space-y-1.5 pt-1">
          <button
            onClick={handleJoin}
            className="w-full py-2.5 rounded-lg bg-[#5865F2] hover:bg-[#4752C4] text-white font-chakra font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg transition-all cursor-pointer"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Intră pe Discord
          </button>
          <button
            onClick={handleCopy}
            className="w-full py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-400 hover:text-white text-xs flex items-center justify-center gap-1 transition-all cursor-pointer"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Link Copiat!' : 'Copiază Invitație'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServerGridCard({ server, onSelect }) {
  const [copied, setCopied] = useState(false);
  const [copiedConsole, setCopiedConsole] = useState(false);
  const address = server.address;

  const { data, isLoading } = useQuery({
    queryKey: ['serverStatusCard', address],
    queryFn: async () => {
      const res = await fetch(`/api/servers/status?address=${encodeURIComponent(address)}`);
      return res.json();
    },
    staleTime: 0,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const isOnline = data && data.online === true;
  const liveName = data?.name?.trim();
  const dbName = server?.name?.trim();
  const serverName = (liveName && liveName !== 'Counter-Strike 1.6 Server' && liveName !== 'Counter-Strike' && liveName !== 'Half-Life')
    ? liveName
    : (dbName || liveName || address);
  const currentMap = data?.map || 'de_dust2';
  const players = data?.players ?? 0;
  const maxPlayers = data?.max_players ?? 32;
  const fillPct = maxPlayers > 0 ? Math.min(100, Math.max(0, Math.round((players / maxPlayers) * 100))) : 0;
  const mapImg = getMapImage(currentMap);
  const dns = server.dns || address;
  const ping = data?.ping ? Math.round(data.ping * 1000) : 18;
  const adminsCount = data?.admins_online ?? 0;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success(`IP Copiat: ${address}`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyConsole = (e) => {
    e.stopPropagation();
    const consoleCmd = `connect ${address}`;
    navigator.clipboard.writeText(consoleCmd);
    setCopiedConsole(true);
    toast.success(`Comandă copiată: "${consoleCmd}" (Paste în consola ~)`);
    setTimeout(() => setCopiedConsole(false), 2000);
  };

  const handleConnect = (e) => {
    e.stopPropagation();
    window.location.href = `steam://connect/${address}`;
  };

  if (isLoading) {
    return (
      <div className="bg-[#0e1017] border border-white/10 rounded-2xl p-4 animate-pulse space-y-3">
        <div className="h-32 bg-white/5 rounded-xl" />
        <div className="h-4 bg-white/5 rounded w-2/3" />
        <div className="h-3 bg-white/5 rounded w-1/3" />
      </div>
    );
  }

  return (
    <div
      onClick={() => onSelect(server)}
      className="group bg-[#090b12] border border-white/10 hover:border-[var(--tf-primary,#ff4f00)]/50 rounded-2xl overflow-hidden shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(255,79,0,0.15)] flex flex-col justify-between cursor-pointer"
    >
      <div>
        {/* Map Header Preview */}
        <div className="relative h-36 bg-zinc-950 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{ backgroundImage: `url(${mapImg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090b12] via-[#090b12]/60 to-transparent" />

          {/* Status Badge */}
          <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/80 backdrop-blur border border-white/10 text-[9px] font-mono font-bold">
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`} />
            <span className={isOnline ? 'text-emerald-400' : 'text-red-400'}>
              {isOnline ? 'CS 1.6 LIVE' : 'OFFLINE'}
            </span>
          </div>

          {/* Map Name Overlay */}
          <span className="absolute bottom-2.5 left-2.5 bg-black/85 border border-white/15 px-2 py-0.5 rounded text-amber-400 font-mono text-[10px] font-extrabold uppercase tracking-wider shadow">
            📍 {currentMap}
          </span>
        </div>

        {/* Card Body */}
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="px-1.5 py-0.2 rounded bg-[var(--tf-primary)]/15 text-[var(--tf-primary)] text-[9px] font-chakra font-black uppercase">
                {server.mod || "CS 1.6"}
              </span>
              <span className="text-[10px] font-mono text-zinc-500">{ping}ms</span>
            </div>
            <h3 className="font-chakra text-sm font-black text-white group-hover:text-[var(--tf-primary)] transition-colors truncate">
              {serverName}
            </h3>
            <div className="flex items-center justify-between text-xs font-mono text-zinc-400 mt-0.5">
              <span className="truncate text-[11px]">{dns}</span>
              <button onClick={handleCopy} className="p-1 hover:text-white transition-colors" title="Copiază IP">
                {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {/* Players Progress Bar */}
          <div>
            <div className="flex justify-between text-[11px] font-mono mb-1">
              <span className="text-zinc-400">Jucători</span>
              <span className="text-white font-bold">{players} <span className="text-zinc-500">/ {maxPlayers}</span></span>
            </div>
            <div className="w-full bg-black/60 rounded-full h-1.5 overflow-hidden border border-white/5">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-1000" 
                style={{ width: `${fillPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card Action Footer */}
      <div className="p-4 pt-0 space-y-1.5">
        <div className="flex gap-1.5">
          <button
            onClick={handleConnect}
            className="flex-1 py-2 rounded-lg bg-[var(--tf-primary)] hover:brightness-110 text-black font-chakra font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1 transition-all shadow-md cursor-pointer"
          >
            <Play className="w-3 h-3 fill-black" /> Intră pe Server
          </button>
          <button
            onClick={handleCopyConsole}
            className="px-2.5 py-2 rounded-lg bg-black/60 hover:bg-black/90 border border-emerald-500/30 text-emerald-400 font-mono text-xs font-bold transition-all cursor-pointer"
            title="Copiază connect IP pentru consolă"
          >
            <Terminal className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Servere() {
  const [selectedServer, setSelectedServer] = useState(null);
  const [viewMode, setViewMode] = useState("all"); // "all" | "table" | "grid"
  const [selectedMod, setSelectedMod] = useState("ALL"); // "ALL" | "RESPAWN" | "PUBLIC" | "MIX" | "WAR3FT" | "FUN"

  const { data: dbServers } = useQuery({
    queryKey: ["serversList"],
    queryFn: async () => (await api.get("/servers/list")).data,
    staleTime: 60000,
  });

  const serverList = dbServers && dbServers.length > 0 ? dbServers : DEFAULT_SERVERS;
  
  const filteredServers = serverList.filter(s => {
    if (selectedMod === "ALL") return true;
    return s.mod === selectedMod || s.game_type === selectedMod;
  });

  const gameServers = filteredServers.filter((s) => s.game_type !== "discord");

  const MOD_FILTERS = [
    { id: "ALL", label: "Toate Modurile CS 1.6" },
    { id: "RESPAWN", label: "Respawn CSDM" },
    { id: "PUBLIC", label: "Clasic / Public" },
    { id: "MIX", label: "Mix 5v5" },
    { id: "WAR3FT", label: "War3FT Heroes" },
    { id: "FUN", label: "Furien & Zombie" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8 space-y-8 animate-fadeIn">
      {/* Main Container: CS 1.6 Server Browser Header */}
      <div className="bg-[#0b0e17] border border-white/10 p-5 sm:p-7 rounded-2xl shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-5">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-[var(--tf-primary,#ff4f00)]/15 border border-[var(--tf-primary,#ff4f00)]/30 flex items-center justify-center text-[var(--tf-primary,#ff4f00)] shrink-0 shadow-lg">
              <Server className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-white font-chakra uppercase tracking-wider">
                Serverele Comunității INDUNGI
              </h1>
              <p className="text-zinc-400 text-xs font-mono mt-0.5">
                // Monitorizare live servere Counter-Strike 1.6 (GoldSrc) & Discord
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 font-mono text-xs flex-wrap">
            {/* View Mode Switcher */}
            <div className="flex items-center bg-black/50 border border-white/10 p-1 rounded-lg">
              <button 
                onClick={() => setViewMode('all')}
                className={`px-3 py-1 rounded-md flex items-center gap-1 text-xs font-chakra font-bold uppercase transition-all cursor-pointer ${viewMode === 'all' ? 'bg-[var(--tf-primary)] text-black shadow-md' : 'text-zinc-400 hover:text-white'}`}
              >
                Complet
              </button>
              <button 
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded-md flex items-center gap-1 text-xs font-chakra font-bold uppercase transition-all cursor-pointer ${viewMode === 'table' ? 'bg-[var(--tf-primary)] text-black shadow-md' : 'text-zinc-400 hover:text-white'}`}
              >
                <List className="w-3.5 h-3.5" /> Tabel
              </button>
              <button 
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 rounded-md flex items-center gap-1 text-xs font-chakra font-bold uppercase transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-[var(--tf-primary)] text-black shadow-md' : 'text-zinc-400 hover:text-white'}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Carduri 3D
              </button>
            </div>

            <div className="px-3 py-1.5 rounded-lg bg-black/40 border border-white/10 text-zinc-300">
              Total Servere: <strong className="text-amber-400">{filteredServers.length}</strong>
            </div>
          </div>
        </div>

        {/* CS 1.6 Mod Filter Chips */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 font-chakra text-xs">
          <span className="text-zinc-500 uppercase font-bold text-[10px] flex items-center gap-1 mr-1">
            <Filter className="w-3 h-3" /> MODURI:
          </span>
          {MOD_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setSelectedMod(f.id)}
              className={`px-3 py-1.5 rounded-lg border uppercase font-bold transition-all cursor-pointer shrink-0 ${
                selectedMod === f.id
                  ? "bg-[var(--tf-primary,#ff4f00)] text-black border-[var(--tf-primary,#ff4f00)] shadow-md"
                  : "bg-white/[0.03] border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Server Rows List (Tabel / Rânduri CS 1.6) */}
        {(viewMode === 'all' || viewMode === 'table') && (
          <div className="space-y-2">
            {filteredServers.map((s, idx) => (
              <ServerStatusRow key={s.id || s.address} server={s} index={idx} onSelect={setSelectedServer} />
            ))}
          </div>
        )}

        {/* Bottom Summary Bar */}
        <ServerSummaryFooter servers={filteredServers} />
      </div>

      {/* Grid Layout below: Cards view + Discord Sidebar Card */}
      {(viewMode === 'all' || viewMode === 'grid') && (
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <main className="flex-1 min-w-0 space-y-5 w-full">
            <h2 className="font-chakra text-lg font-black uppercase tracking-wider text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-orange-500" /> Hărți Active & Meciuri în Desfășurare CS 1.6
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {gameServers.map((s) => (
                <ServerGridCard key={s.id || s.address} server={s} onSelect={setSelectedServer} />
              ))}
            </div>
          </main>

          <aside className="w-full lg:w-96 shrink-0 space-y-6">
            <DiscordSidebarCard inviteCode="CrGC77X8W" />
          </aside>
        </div>
      )}

      {/* Server Real-Time Details Modal with CS 1.6 Match Score & Admin Control */}
      {selectedServer && (
        <ServerDetailsModal server={selectedServer} onClose={() => setSelectedServer(null)} />
      )}
    </div>
  );
}
